import React, { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { createKitchen } from "../../../redux/actions/kitchenActions";
import { useLanguage } from "../../../context/LanguageContext";

const SectionIFoodHygiene = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const dispatch = useDispatch();
  const { t } = useLanguage();

  const [form, setForm] = useState({
    i1: "",
    i1Remarks: "",
  });

  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    
    // Clear error when user makes a selection
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    // Required field validations
    if (!form.i1) newErrors.i1 = t.global.err_a1;
    if (form.i1 === "other" && !form.i1Remarks.trim()) newErrors.i1Remarks = "Please specify other materials used";
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const packagingToInt = (value) => {
    switch (value) {
      case "aluminium":
        return 1;
      case "plastic":
        return 2;
      case "other":
        return 3;
      default:
        return null;
    }
  };
  
  /* ================================
     SAVE TO LOCAL STORAGE
  ================================ */
  const handleSubmit = () => {
    if (!validateForm()) return;

    try {
      const fd = new FormData();

      // =====================
      // Master + Location
      // =====================
      fd.append("district_id", state?.district_id);
      fd.append("municipality_id", state?.municipality_id);
      fd.append("ward_id", state?.ward_id);
      fd.append("area_id", state?.area_id);
      fd.append("canteen_type_id", state?.canteen_type_id);
      fd.append("latitude", state?.latitude);
      fd.append("longitude", state?.longitude);
      fd.append("reporting_date", new Date().toISOString().split("T")[0]);

      // =====================
      // Section I – Food Hygiene
      // =====================
      fd.append("food_hygn_I1", packagingToInt(form.i1));
      if (form.i1 === "other" && form.i1Remarks.trim()) {
        fd.append("food_hygn_I1_remarks", form.i1Remarks.trim());
      }
      fd.append("current_section", "food_hygine");

      // =====================
      // Dispatch API
      // =====================
      dispatch(createKitchen(fd));

      // =====================
      // Save locally
      // =====================
      const surveyData = JSON.parse(localStorage.getItem("surveyData") || "{}");
      surveyData.sectionI = form;
      localStorage.setItem("surveyData", JSON.stringify(surveyData));

      const completedSections = JSON.parse(localStorage.getItem("completedSections") || "{}");
      completedSections.i = true;
      localStorage.setItem("completedSections", JSON.stringify(completedSections));

      // =====================
      // Navigate & feedback
      // =====================
      navigate("/survey/sections", { state });

    } catch (err) {
      console.error("Error saving Section I:", err);
      alert(t.global.alert_server_error);
    }
  };

  const packagingOptions = [
    { value: "aluminium", label: `1. ${t.modules.kitchen.sections.I.options.packagingMaterial[0]}` },
    { value: "plastic", label: `2. ${t.modules.kitchen.sections.I.options.packagingMaterial[1]}` },
    { value: "other", label: `3. ${t.modules.kitchen.sections.I.options.packagingMaterial[2]}` }
  ];

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{t.modules.kitchen.sections.I.title}</h2>
              <p className="text-gray-600">{t.modules.kitchen.sections.I.subtitle || "Food packaging materials"}</p>
            </div>
            <div className="text-sm bg-pink-100 text-pink-800 px-3 py-1 rounded-full">
              {state?.canteenId ? `${t.global.canteen_id_label} ${state.canteenId}` : 
               state?.kioskId ? `${t.global.canteen_id_label} ${state.kioskId}` : t.global.na}
            </div>
          </div>

          <div className="space-y-6">
            {/* I.1 */}
            <div className="border rounded-lg p-4">
              <label className="block font-medium mb-3">
                I.1 {t.modules.kitchen.sections.I.questions.packagingMaterial}<span className="text-red-500">*</span>
              </label>
              <div className="space-y-3 mb-4">
                {packagingOptions.map((option) => (
                  <label 
                    key={option.value}
                    className={`flex items-center p-3 border rounded cursor-pointer ${
                      form.i1 === option.value ? 'bg-blue-50 border-blue-300' : ''
                    } ${errors.i1 ? 'border-red-300' : ''}`}
                  >
                    <input
                      type="radio"
                      name="i1"
                      value={option.value}
                      checked={form.i1 === option.value}
                      onChange={handleChange}
                      className="mr-3 h-5 w-5"
                      required
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
              {errors.i1 && <p className="text-red-500 text-sm mt-2">{errors.i1}</p>}
              
              {form.i1 === "other" && (
                <div className="mt-4">
                  <label className="block text-sm font-medium mb-2">
                    {t.global.remarks_label} <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    name="i1Remarks"
                    placeholder="Specify other packaging materials used..."
                    className={`w-full border p-3 rounded ${errors.i1Remarks ? 'border-red-500' : ''}`}
                    rows="3"
                    value={form.i1Remarks}
                    onChange={handleChange}
                    required={form.i1 === "other"}
                  />
                  {errors.i1Remarks && <p className="text-red-500 text-sm mt-1">{errors.i1Remarks}</p>}
                </div>
              )}
            </div>

            {/* Required fields note */}
            <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-yellow-700">
                    <span className="text-red-500">*</span> {t.global.err_complete_all}
                  </p>
                </div>
              </div>
            </div>

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <button
                onClick={() => navigate("/survey/sections", { state })}
                className="px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition"
              >
                {t.global.back_to_sections}
              </button>
              <button
                onClick={handleSubmit}
                className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition"
              >
                {t.global.submit_section_i || "Save Section I"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SectionIFoodHygiene;